<!--
 * @Author       : 花裤衩<白永明><yongming.bai@outlook.com>
 * @Date         : 2026-07-09 11:41:32 +0800
 * @LastEditors  : 花裤衩<白永明><yongming.bai@outlook.com>
 * @LastEditTime : 2026-07-09 11:41:45 +0800
 * @FilePath     : /ai-agent-lumina/src/components/global/popup/src/izt-popup.vue
 * @Description  : 通用弹窗组件，支持上下左右居中弹出
-->
<style lang="scss" scoped>
.izt-popup {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 999;
  visibility: hidden;

  &.visible {
    visibility: visible;
  }

  // 遮罩层
  &__mask {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.5);
    opacity: 0;
    transition: opacity 0.3s ease;
    z-index: 1;

    &.show {
      opacity: 1;
    }
  }

  // 内容容器
  &__content {
    position: absolute;
    transition: transform 0.3s ease, opacity 0.3s ease;
    background-color: var(--izt-head-color);
    overflow: hidden;
    z-index: 2;

    // 顶部弹出
    &.top {
      left: 0;
      right: 0;
      top: 0;
      transform: translateY(-100%);

      &.show {
        transform: translateY(0);
      }
    }

    // 底部弹出
    &.bottom {
      left: 0;
      right: 0;
      bottom: 0;
      transform: translateY(100%);
      display: flex;
      flex-direction: column;

      &.show {
        transform: translateY(0);
      }
    }

    // 左侧弹出
    &.left {
      left: 0;
      top: 0;
      bottom: 0;
      transform: translateX(-100%);
      display: flex;
      flex-direction: column;

      &.show {
        transform: translateX(0);
      }
    }

    // 右侧弹出
    &.right {
      right: 0;
      top: 0;
      bottom: 0;
      transform: translateX(100%);
      display: flex;
      flex-direction: column;

      &.show {
        transform: translateX(0);
      }
    }

    // 居中弹出
    &.center {
      left: 50%;
      top: 50%;
      transform: translate(-50%, -50%) scale(0.8);
      opacity: 0;
      border-radius: 24px;
      overflow: hidden;

      &.show {
        transform: translate(-50%, -50%) scale(1);
        opacity: 1;
      }
    }

    // 头部
    &__head {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 24px;
      gap: 0 24px;
      box-sizing: border-box;
      min-width: 0;
      width: 100%;

      &__title {
        flex: 1;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        font-weight: bold;
        font-size: 28px;
        color: var(--izt-background-text-color-65);
      }

      &__close {
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 8px;
      }
    }

    // 内容体
    &__body {
      width: fit-content;
      min-width: 0;
      min-height: 0;
      box-sizing: border-box;
      overflow: hidden;
    }

    // bottom/left/right 模式下内容体自适应
    &.bottom &__body,
    &.left &__body,
    &.right &__body {
      flex: 1;
      width: 100%;
    }
  }
}
</style>

<template>
  <div class="izt-popup" :class="{ visible: visible }">
    <!-- 遮罩层 -->
    <div class="izt-popup__mask" :class="{ show: showContent }" @click.stop="onMaskClick"></div>

    <!-- 内容区域 -->
    <div class="izt-popup__content" :class="[mode, { show: showContent }]" :style="contentStyle">
      <!-- 头部 -->
      <div class="izt-popup__content__head" v-if="title || $slots.title">
        <span class="izt-popup__content__head__title">
          <slot name="title">{{ title }}</slot>
        </span>
        <div class="izt-popup__content__head__close" @click.stop="close()">
          <izt-icon class-name="icon-guanbi" />
        </div>
      </div>
      <!-- 内容体 -->
      <div class="izt-popup__content__body">
        <slot></slot>
      </div>
    </div>
  </div>
</template>

<script setup name="izt-popup">
  import { ref, computed, watch, nextTick } from 'vue'

  defineOptions({ name: 'izt-popup' })

  const props = defineProps({
    /**
     * 弹出方向
     * @values top, bottom, left, right, center
     */
    mode: {
      type: String,
      default: 'bottom',
      validator: (value) => ['top', 'bottom', 'left', 'right', 'center'].includes(value)
    },
    /**
     * 弹窗标题
     */
    title: {
      type: String,
      default: ''
    },
    /**
     * 是否显示弹窗（支持 v-model:show）
     */
    show: {
      type: Boolean,
      default: false
    },
    /**
     * 点击遮罩是否关闭
     */
    closeOnClickMask: {
      type: Boolean,
      default: true
    },
    /**
     * 动画时长（毫秒）
     */
    duration: {
      type: Number,
      default: 300
    },
    /**
     * 是否显示遮罩
     */
    overlay: {
      type: Boolean,
      default: true
    }
  })

  const emit = defineEmits(['update:show', 'change', 'maskClick'])

  const visible = ref(false)
  const showContent = ref(false)

  // 内容样式
  const contentStyle = computed(() => {
    return {
      transitionDuration: `${props.duration}ms`
    }
  })

  /**
   * 打开弹窗
   */
  const open = () => {
    visible.value = true
    nextTick(() => {
      setTimeout(() => {
        showContent.value = true
        emit('change', { show: true })
      }, 50)
    })
  }

  /**
   * 关闭弹窗
   */
  const close = () => {
    showContent.value = false
    emit('update:show', false)
    setTimeout(() => {
      visible.value = false
      emit('change', { show: false })
    }, props.duration)
  }

  /**
   * 点击遮罩
   */
  const onMaskClick = () => {
    emit('maskClick')
    if (props.closeOnClickMask) {
      close()
    }
  }

  // 监听 show prop 变化
  watch(() => props.show, (val) => {
    if (val) {
      open()
    } else {
      close()
    }
  }, { immediate: true })

  // 暴露方法给父组件
  defineExpose({ open, close })
</script>
